import React, { useEffect, useState } from "react";
import { Icon, Spin } from "antd";
import { ChevronRightIcon } from "../../../../common/CustomIcon";
import { fetchBillAmount_v2 } from "../../../../api/OrdersApi";
import { fetchNonBillableAccounts } from "../../../../api/Account";
import { saveInvoice } from "../../../../api/Billing";
import { checkNegitive, randomNumber } from "../../../../common/Common";
import OrderBillingDetails from "../../../../components/billing/OrderBillingDetails";
import ShowInvoice from "../../../../components/billing_screen/ShowInvoice";
import { isOrderAccountBillable } from "../../../../components/orders/helpers";
import { isInvoiceLocked } from "../../../../helpers/billing";
import { checkAccessExistance } from "../../../../helpers/common";

function EstimateBill(props) {
  const { currentOrder , setViewTab, setActiveKey, goToTab, setShowDrawer, estimateRefreshKey, setEstimateRefreshKey, onUnmount = () => {}} = props;

  const [data, setData] = useState();
  const [isAccountNonBillable, setIsAccountNonBillable] = useState(false);
  const [loading, setLoading] = useState(false);
  const isLocked = isInvoiceLocked(currentOrder?.customer_order_details);
  const canOpenBillingFromOrder = checkAccessExistance("BILLING_FROM_ORDER");

  const getBillAmount = async (showLoading = false) => {
    if (!currentOrder?.customer_order_details) return;
    const { customer_order_id, organization_id, status } =
      currentOrder.customer_order_details;
    if (showLoading) setLoading(true);
    try {
      const result = await fetchBillAmount_v2(
        customer_order_id,
        organization_id,
        status
      );
      if (result.success) {
        setData(result.data);
      }
    } catch (error) {
      console.error("Error fetching bill amount:", error);
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => {
    if(estimateRefreshKey){
      getBillAmount();
    }
  }, [estimateRefreshKey]);

  useEffect(() => {
    getBillAmount();
  }, [currentOrder]);

  const fetchBillabilityStatus = async () => {
    const orgId = currentOrder?.customer_order_details?.organization_id;
    if (!orgId) {
      setIsAccountNonBillable(false);
      return;
    }

    try {
      const result = await fetchNonBillableAccounts(orgId);
      if (result.success) {
        const orderAccount = currentOrder?.account || {
          account_id: currentOrder?.account?.account_id,
          account_code: currentOrder?.account?.account_code || currentOrder?.account_code,
        };
        setIsAccountNonBillable(
          !isOrderAccountBillable(orderAccount, result.accounts)
        );
      } else {
        setIsAccountNonBillable(false);
      }
    } catch (error) {
      console.error("Error fetching non-billable accounts:", error);
      setIsAccountNonBillable(false);
    }
  };

  useEffect(() => {
    fetchBillabilityStatus();
  }, [currentOrder?.id, currentOrder?.account?.account_id]);

  const handleNavigate = () => {
    if (currentOrder?.status == "COMPLETED") {
      if (!checkAccessExistance("BILLING_FROM_ORDER")) {
        return;
      }
      setViewTab("invoice"), setShowDrawer(true);
    } else {
      setActiveKey("estimate");
      goToTab("billing");
    }
  };

  const handleRecalculate = async () => {
    if (!currentOrder?.customer_order_details) return;
    setLoading(true);
    const { customer_order_id } = currentOrder.customer_order_details;
    const today = new Date().toISOString().split('T')[0];
    try {
      const result = await saveInvoice({ order_id: customer_order_id, is_for_invoice_quote: "true", current_date: today });
      if (result.success) {
        await getBillAmount(true);
      }
    } catch (error) {
      console.error("Error recalculating:", error);
    } finally {
      setLoading(false);
    }
  };

  // Wait until billability is determined
  if (isAccountNonBillable === null) {
    return null;
  }

  // Hide billing card for non-billable accounts
  if (isAccountNonBillable) {
    return null;
  }

  return (
    <div>
      <Spin spinning={loading}>
        {data && ( <div className="billing-card" style={{ pointerEvents: loading ? "none" : "auto", opacity: loading ? 0.6 : 1 }}>
          <div className="card-header">
            <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              {currentOrder?.status === "COMPLETED" ? "Billing" : "Estimate"}
              {!isLocked && (<Icon
                type="reload"
                spin={loading}
                style={{
                  cursor: loading ? "not-allowed" : "pointer",
                  color: "#586FCC",
                  fontSize: "14px"
                }}
                onClick={(e) => {
                  if (loading) return;
                  e.stopPropagation();
                  handleRecalculate();
                }}
              />)}
            </span>
            {currentOrder?.status === "COMPLETED" ? (
            <OrderBillingDetails
              onUnmount={onUnmount}
              record={{...currentOrder, customer_order_id : currentOrder.id}}
              showActions = {false}
              callFrom = "newUIScreen"
              onClose={() => setEstimateRefreshKey(randomNumber())}
              frameToDisplay = {"drawer"}
             />
            )
            :  (
            <span
              data-testid="view-estimate"
              onClick={handleNavigate}
              style={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "5px",
              }}
            >
              <ChevronRightIcon
                className="chevron-right"
                height="16"
                width="16"
                color="#586FCC"
              />
            </span>
  )}
          </div>
          {Object.entries(data || {}).filter(([key, value]) => key !== 'total').map(([key, value]) => (
            <div className="billing-item" key={key}>
              <span>{key}</span>
              <span style={{ fontWeight: "700" }}>{checkNegitive(value)}</span>
            </div>
          ))}
          <div className="billing-item">
            <span style={{ fontSize: "16px", fontWeight: "800" }}>Total</span>
            <span
              style={{ color: "#586FCC", fontWeight: "700", fontSize: "16px" }}
            >
              {currentOrder?.status == "COMPLETED"
                ? checkNegitive(data.total || 0.0) // checkNegitive(currentOrder?.customer_order_details?.total_amount || 0.0)
                : checkNegitive(data.total || 0.0)}
            </span>
          </div>
          {currentOrder?.status === "COMPLETED" && currentOrder?.customer_order_details?.invoice_number && (
            <div className="billing-item">
              <span>Invoice</span>
                {canOpenBillingFromOrder ? (
                <ShowInvoice
                  account={currentOrder?.account?.account_id}
                  invoice_no={
                    currentOrder?.customer_order_details?.invoice_number
                  }
                  customer_order_id={currentOrder?.id}
                  style={{ marginTop: -6 }}
                  account_invoice_id={currentOrder?.customer_order_details?.invoice_id}
                  accountInvoices={currentOrder?.customer_order_details}
                  refreshList={() => {}}
                  showInvoiceActions={false}
                  approvalDate={
                    currentOrder?.customer_order_details?.invoice_approval_date
                  }
                  renderType="text"
                />
                ) : (
                  <span style={{ fontWeight: "700" }}>
                    {currentOrder.customer_order_details.invoice_number}
                  </span>
                )}
              {/* <OrderBillingDetails
                  record={{...currentOrder, customer_order_id : currentOrder.id,invoice_number: currentOrder?.customer_order_details?.invoice_number}}
                  showActions = {false}
                  onClose={() => setEstimateRefreshKey(randomNumber())}
                  keytoShow = {"invoice_number"}
                  frameToDisplay = {"drawer"}
                  styleObj={{ color: "#586FCC", fontWeight: "700", fontSize: "16px", cursor: "pointer" }}
                  callFrom = "newUIScreen"
                /> */}
              {/* <span
                style={{ color: "#586FCC", fontWeight: "700", fontSize: "16px", cursor: "pointer" }}
                onClick={() => {
                  setViewTab("invoice"), setShowDrawer(true);
                }}
              >
                {`${'#'+ currentOrder?.customer_order_details?.invoice_number}` || "-"}
              </span> */}
            </div>
          )}
        </div>)}
      </Spin>
    </div>
  );
}

export default EstimateBill;
